import { useEffect, useState } from "react";
import Modal from "react-modal";
import { ActiveButton, EditButton, LockButton } from "../../components/buttons";
import { Image } from "../../components/inputs";
import { AWS_DIRECTORIES } from "../../constants/Common";
import { endpoints } from "../../constants/Endpoints";
import useData from "../../hooks/useData";
import { instance } from "../../services/https/inceptors";
import { exportToExcel } from "../../utilities/excel";
import { BASE, SUBJECTS, useFetch } from "../../utilities/apis";

const ViewSubjects = () => {
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState("");
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedSubject, setEditedSubject] = useState({});
  const [imageSubject, setImageSubject] = useState({});
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { getExams, getDepartmentByExam, getSubjectByDepartment } = useData();
  const { update } = useFetch();

  const modalStyles = {
    content: {
      width: "40%",
      top: "53%",
      zIndex: 1,
      maxHeight: "calc(100vh - 7rem)",
      position: "fixed",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
    },
  };

  useEffect(() => {
    const fetchExams = async () => {
      const exams = await getExams();
      if (exams) setExams(exams);
    };

    fetchExams();
  }, []);

  useEffect(() => {
    if (selectedExam) {
      const fetchDepartments = async () => {
        const departments = await getDepartmentByExam(selectedExam);
        if (departments) setDepartments(departments);
      };

      fetchDepartments();
    }
  }, [selectedExam]);

  useEffect(() => {
    if (selectedExam && selectedDepartment) {
      const fetchSubjects = async () => {
        const subjects = await getSubjectByDepartment(selectedDepartment);
        if (subjects) setSubjects(subjects);
      };

      fetchSubjects();
    }
  }, [selectedExam, selectedDepartment]);

  const handleEditClick = (index, subject) => {
    if (editingIndex === null) {
      setEditingIndex(index);
      setEditedSubject({
        code: subject.code,
        title: subject.title,
        order: subject.order ?? 0,
        yearRange: subject.yearRange || {},
      });
    } else if (editingIndex === index) {
      handleUpdate(subject, editedSubject, true);
    } else {
      setEditingIndex(index);
      setEditedSubject({
        code: subject.code,
        title: subject.title,
        order: subject.order ?? 0,
        yearRange: subject.yearRange || {},
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedSubject((prev) => {
      const updatedSubject = { ...prev };
      if (name === "order") {
        updatedSubject.order = Number(value);
        return updatedSubject;
      }
      const keys = name.split(".");
      let temp = updatedSubject;
      for (let i = 0; i < keys.length - 1; i++) {
        temp = temp[keys[i]] = temp[keys[i]] || {};
      }
      temp[keys[keys.length - 1]] = value;
      return updatedSubject;
    });
  };

  const handleUpdate = (subject, data, edit = false) => {
    update(`${BASE}update/subject/?id=${subject._id}`, data, (error, res) => {
      if (error) return console.log(error);
      const clone = [...subjects];
      const index = clone.findIndex((e) => e._id === subject._id);
      clone[index] = res;
      setSubjects(clone);

      if (edit) {
        setEditingIndex(null);
        setEditedSubject({});
      }
    });
  };

  const handleImageModal = (subject) => {
    setImageSubject(subject);
    setOpen(true);
  };

  const closeImageModal = () => {
    setImageSubject({});
    setOpen(false);
  };

  const handleImageChange = (link) => {
    const data = { image: link };
    handleUpdate(imageSubject, data);
    setImageSubject({ ...imageSubject, image: link });
  };

  const handleToggleAllDepartment = (subject) => {
    update(`${SUBJECTS}toggle-all-department`, { _id: subject._id }, (error, res) => {
      if (error) return console.log(error);
      const clone = [...subjects];
      const index = clone.findIndex((e) => e._id === subject._id);
      clone[index] = res;
      setSubjects(clone);
    });
  };

  const handleExport = async () => {
    setExporting(true);
    const { data: rows } = await instance.get(
      `${endpoints.curriculumExport}?exam=${selectedExam}&department=${selectedDepartment}`
    );
    exportToExcel(
      rows,
      [
        { header: "Exam", key: "exam" },
        { header: "Department", key: "department" },
        { header: "Subject", key: "subject" },
        { header: "Chapter", key: "chapter" },
        { header: "Topic", key: "topic" },
      ],
      "curriculum.xlsx"
    );
    setExporting(false);
  };

  return (
    <div>
      <form>
        <div className="mb-3">
          <label htmlFor="exam" className="form-label">
            Select exam
          </label>
          <select
            className="form-select"
            id="exam"
            value={selectedExam}
            onChange={(e) => setSelectedExam(e.target.value)}
          >
            <option value="">Select exam</option>
            {exams.map((exam, index) => {
              return (
                <option key={index} value={exam._id}>
                  {exam.title}
                </option>
              );
            })}
          </select>
        </div>

        <div className="mb-3">
          <label htmlFor="department" className="form-label">
            Select department
          </label>
          <select
            className="form-select"
            id="department"
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
          >
            <option value="">Select department</option>
            {departments.map((department, index) => {
              return (
                <option key={index} value={department._id}>
                  {department.title}
                </option>
              );
            })}
          </select>
        </div>
      </form>

      {subjects.length > 0 && (
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-success btn-sm" onClick={handleExport} disabled={exporting}>
            {exporting ? "Exporting..." : "Export to Excel"}
          </button>
        </div>
      )}

      <table className="table">
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Rank</th>
            <th scope="col">Subject code</th>
            <th scope="col">Subject title</th>
            <th scope="col">Year range</th>
            <th scope="col">All Dept</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {subjects.map((subject, index) => {
            return (
              <tr key={index}>
                <th scope="row">{index + 1}</th>

                <td>
                  {editingIndex === index ? (
                    <input
                      type="number"
                      name="order"
                      className="table-control"
                      style={{ width: "70px" }}
                      value={editedSubject.order}
                      onChange={handleInputChange}
                    />
                  ) : (
                    subject.order ?? 0
                  )}
                </td>

                <td>
                  {editingIndex === index ? (
                    <input
                      type="text"
                      name="code"
                      className="table-control"
                      value={editedSubject.code}
                      onChange={handleInputChange}
                    />
                  ) : (
                    subject.code
                  )}
                </td>

                <td>
                  {editingIndex === index ? (
                    <input
                      type="text"
                      name="title"
                      className="table-control"
                      value={editedSubject.title}
                      onChange={handleInputChange}
                    />
                  ) : (
                    subject.title
                  )}
                </td>

                <td>
                  {editingIndex === index ? (
                    <div className="d-flex items-center gap-2">
                      <input
                        type="text"
                        name="yearRange.from"
                        className="table-control"
                        style={{ width: "100px" }}
                        value={
                          editedSubject.yearRange
                            ? editedSubject.yearRange.from
                            : ""
                        }
                        onChange={handleInputChange}
                      />
                      -
                      <input
                        type="text"
                        name="yearRange.to"
                        className="table-control"
                        style={{ width: "100px" }}
                        value={
                          editedSubject.yearRange
                            ? editedSubject.yearRange.to
                            : ""
                        }
                        onChange={handleInputChange}
                      />
                    </div>
                  ) : (
                    <>
                      {subject.yearRange && (
                        <>
                          {subject.yearRange.from} - {subject.yearRange.to}
                        </>
                      )}
                    </>
                  )}
                </td>

                <td>
                  <button
                    className={`btn btn-sm ${subject.AllDepartment ? "btn-success" : "btn-secondary"}`}
                    onClick={() => handleToggleAllDepartment(subject)}
                  >
                    {subject.AllDepartment ? "Yes" : "No"}
                  </button>
                </td>

                <td>
                  <EditButton
                    className="me-3"
                    editing={editingIndex === index}
                    onClick={() => handleEditClick(index, subject)}
                  />

                  <ActiveButton
                    className="me-3"
                    active={subject.display}
                    onClick={() =>
                      handleUpdate(subject, { display: !subject.display })
                    }
                  />

                  <span
                    className="action me-3"
                    onClick={() => handleImageModal(subject)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-image-fill"
                      viewBox="0 0 16 16"
                    >
                      <path d="M.002 3a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2zm1 9v1a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062zm5-6.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0" />
                    </svg>
                  </span>

                  <LockButton
                    locked={subject.lock}
                    onClick={() =>
                      handleUpdate(subject, { lock: !subject.lock })
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Modal isOpen={open} onRequestClose={closeImageModal} style={modalStyles}>
        <h4>{imageSubject.code}</h4>

        <div className="my-3">
          {imageSubject.image ? (
            <img
              src={imageSubject.image}
              alt={imageSubject.code}
              className="img-thumbnail"
            />
          ) : (
            "No image"
          )}
        </div>

        <div>
          <Image
            directory={AWS_DIRECTORIES.SUBJECT}
            onUpload={handleImageChange}
          />
        </div>
      </Modal>
    </div>
  );
};

export default ViewSubjects;
